import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Grid, Typography, Avatar, Chip, IconButton, 
  TextField, Button, MenuItem, CircularProgress, Alert, alpha 
} from '@mui/material';
import { 
  WhatsApp, Email, Business, Person, ArrowBack, 
  Send, History, LocationOn, AccessTime, Star,
  Phone, Chat, Assignment, Badge, EventNote,
  TrendingUp, CalendarToday, AttachMoney
} from '@mui/icons-material';
import './ProspectoDetalle.css';

// SERVICIOS
import { getProspectoById, getSeguimientos, createSeguimiento } from '../../services/crmService';

const ProspectoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // ESTADOS PRINCIPALES
  const [prospecto, setProspecto] = useState(null);
  const [oportunidadId, setOportunidadId] = useState(null);
  const [seguimientos, setSeguimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  
  // FORMULARIO DE SEGUIMIENTO
  const [tipo, setTipo] = useState('nota');
  const [comentario, setComentario] = useState('');

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await getProspectoById(id);
      const data = response.data?.data || response.data;
      setProspecto(data);

      // Buscar ID de Oportunidad
      let oppId = null;
      if (data.oportunidades && data.oportunidades.length > 0) {
        oppId = data.oportunidades[0].id;
        setOportunidadId(oppId);
        
        // Cargar seguimientos
        const segResponse = await getSeguimientos(oppId);
        setSeguimientos(segResponse.data?.data || []);
      } else if (data.oportunidad_id) {
        oppId = data.oportunidad_id;
        setOportunidadId(oppId);
        const segResponse = await getSeguimientos(oppId);
        setSeguimientos(segResponse.data?.data || []);
      }
    } catch (error) {
      console.error("Error cargando detalle:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarDatos(); }, [id]);

  const handleEnviarSeguimiento = async () => {
    if (!comentario.trim()) return;
    if (!oportunidadId) {
      alert("⚠️ No se puede guardar: Este cliente no tiene una oportunidad activa.");
      return;
    }

    try {
      setEnviando(true);
      const payloadSeguimiento = {
        oportunidad_id: oportunidadId,
        tipo: tipo,
        descripcion: comentario.trim() 
      };

      await createSeguimiento(payloadSeguimiento);
      setComentario('');
      await cargarDatos();
    } catch (error) {
      console.error("Error al guardar seguimiento:", error.response?.data);
      alert("❌ Error: " + (error.response?.data?.error || "Faltan campos obligatorios"));
    } finally {
      setEnviando(false);
    }
  };

  const getTipoIcon = (tipo) => {
    switch(tipo) {
      case 'llamada': return <Phone className="tipo-icon" />;
      case 'whatsapp': return <WhatsApp className="tipo-icon" style={{ color: '#25D366' }} />;
      case 'email': return <Email className="tipo-icon" />;
      case 'reunion': return <Business className="tipo-icon" />;
      case 'visita_obra': return <LocationOn className="tipo-icon" />;
      default: return <EventNote className="tipo-icon" />;
    }
  };

  const getTipoColor = (tipo) => {
    const colores = {
      llamada: '#3b82f6',
      whatsapp: '#25D366',
      email: '#f59e0b',
      reunion: '#8b5cf6',
      visita_obra: '#10b981',
      nota: '#6b7280'
    };
    return colores[tipo] || '#6b7280';
  };

  const getEstatusColor = (estatus) => {
    const colores = {
      nuevo: '#10b981',
      contactado: '#3b82f6',
      calificado: '#8b5cf6',
      no_calificado: '#ef4444',
      archivado: '#6b7280'
    };
    return colores[estatus] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="prospecto-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando expediente del prospecto...</p>
        </div>
      </div>
    );
  }

  if (!prospecto) {
    return (
      <div className="prospecto-container">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <h3>No se encontró la información del prospecto.</h3>
          <button onClick={() => navigate('/crm/pipeline')} className="btn-primary">
            Volver al Pipeline
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prospecto-container">
      {/* HEADER PRINCIPAL */}
      <div className="prospecto-header">
        <div className="header-left">
          <IconButton 
            onClick={() => navigate('/crm/pipeline')} 
            className="btn-back"
          >
            <ArrowBack />
          </IconButton>
          <div className="header-info">
            <h1>{prospecto.nombre}</h1>
            <p className="subtitle">
              Expediente #{prospecto.id} • Registrado el {new Date(prospecto.fecha_registro).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="header-badges">
          <span 
            className="estatus-badge" 
            style={{ backgroundColor: getEstatusColor(prospecto.estatus) }}
          >
            {prospecto.estatus?.toUpperCase()}
          </span>
          <span className="tipo-badge">
            <Star fontSize="small" /> {prospecto.proyecto_tipo}
          </span>
        </div>
      </div>

      {/* ESTADÍSTICAS RÁPIDAS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e0e7ff' }}>
            <Phone />
          </div>
          <div className="stat-info">
            <span className="stat-label">Teléfono</span>
            <span className="stat-value">{prospecto.telefono || 'No disponible'}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d1fae5' }}>
            <Email />
          </div>
          <div className="stat-info">
            <span className="stat-label">Email</span>
            <span className="stat-value-small">{prospecto.email || 'No disponible'}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <Business />
          </div>
          <div className="stat-info">
            <span className="stat-label">Empresa</span>
            <span className="stat-value-small">{prospecto.empresa || 'Particular'}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fce7f3' }}>
            <AttachMoney />
          </div>
          <div className="stat-info">
            <span className="stat-label">Presupuesto Est.</span>
            <span className="stat-value">
              {prospecto.presupuesto_estimado 
                ? `$${parseFloat(prospecto.presupuesto_estimado).toLocaleString()}` 
                : 'No definido'}
            </span>
          </div>
        </div>
      </div>

      <div className="prospecto-grid">
        {/* COLUMNA IZQUIERDA: INFORMACIÓN DEL EXPEDIENTE */}
        <div className="columna-info">
          {/* Tarjeta de Información Personal */}
          <div className="info-card">
            <div className="card-header-section">
              <Person className="section-icon" />
              <h3>Información Personal</h3>
            </div>
            <div className="info-list">
              <div className="info-row">
                <span className="info-label">Nombre completo</span>
                <span className="info-value">{prospecto.nombre}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Teléfono móvil</span>
                <span className="info-value">
                  {prospecto.telefono ? (
                    <a href={`tel:${prospecto.telefono}`} className="link-phone">
                      <Phone fontSize="small" /> {prospecto.telefono}
                    </a>
                  ) : 'No disponible'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Correo electrónico</span>
                <span className="info-value">
                  {prospecto.email ? (
                    <a href={`mailto:${prospecto.email}`} className="link-email">
                      <Email fontSize="small" /> {prospecto.email}
                    </a>
                  ) : 'No disponible'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Dirección</span>
                <span className="info-value">
                  <LocationOn fontSize="small" /> {prospecto.direccion || 'Sin dirección'}
                </span>
              </div>
            </div>
          </div>

          {/* Tarjeta de Datos del Negocio */}
          <div className="info-card">
            <div className="card-header-section">
              <Business className="section-icon" />
              <h3>Datos del Negocio</h3>
            </div>
            <div className="info-list">
              <div className="info-row">
                <span className="info-label">Empresa / Razón Social</span>
                <span className="info-value">{prospecto.empresa || 'Cliente Particular'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Fuente de contacto</span>
                <span className="info-value-badge">{prospecto.fuente}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Tipo de proyecto</span>
                <span className="info-value-badge" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                  {prospecto.proyecto_tipo}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Vendedor asignado</span>
                <div className="user-assign">
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#3b82f6' }}>
                    {prospecto.asignado?.nombre?.[0] || 'V'}
                  </Avatar>
                  <span>{prospecto.asignado?.nombre || 'Pendiente'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Oportunidades Vinculadas */}
          {prospecto.oportunidades && prospecto.oportunidades.length > 0 && (
            <div className="info-card oportunidades-card">
              <div className="card-header-section">
                <TrendingUp className="section-icon" />
                <h3>Oportunidades Activas</h3>
              </div>
              <div className="oportunidades-list">
                {prospecto.oportunidades.map(opp => (
                  <div key={opp.id} className="oportunidad-item">
                    <div className="opp-header">
                      <span className="opp-id">Opp #{opp.id}</span>
                      <span 
                        className="opp-etapa" 
                        style={{ backgroundColor: getEstatusColor(opp.etapa) }}
                      >
                        {opp.etapa}
                      </span>
                    </div>
                    <div className="opp-details">
                      <span className="opp-valor">
                        {opp.valor_estimado ? `$${parseFloat(opp.valor_estimado).toLocaleString()}` : 'Sin valor'}
                      </span>
                      <span className="opp-prob">{opp.probabilidad}% probabilidad</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!oportunidadId && (
            <Alert severity="warning" className="alert-warning">
              Este cliente no tiene una oportunidad vinculada. Debes crear una para registrar seguimientos.
            </Alert>
          )}
        </div>

        {/* COLUMNA DERECHA: SEGUIMIENTO Y ACTIVIDADES */}
        <div className="columna-seguimiento">
          
          {/* Formulario de Nueva Actividad */}
          <div className="seguimiento-form-card">
            <div className="form-header">
              <Chat className="form-icon" />
              <h3>Registrar Nueva Actividad</h3>
            </div>
            
            <div className="form-grid">
              <div className="form-field tipo-field">
                <label>Tipo de actividad</label>
                <TextField
                  select
                  fullWidth
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  size="small"
                  variant="outlined"
                  className="select-tipo"
                >
                  <MenuItem value="nota">📝 Nota</MenuItem>
                  <MenuItem value="llamada">📞 Llamada</MenuItem>
                  <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                  <MenuItem value="email">📧 Correo</MenuItem>
                  <MenuItem value="visita_obra">🚗 Visita a obra</MenuItem>
                  <MenuItem value="reunion">🤝 Reunión</MenuItem>
                </TextField>
              </div>
              
              <div className="form-field comentario-field">
                <label>Descripción de la gestión</label>
                <div className="input-group">
                  <TextField
                    fullWidth
                    placeholder="Escribe los detalles de la interacción..."
                    multiline
                    rows={3}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    disabled={!oportunidadId || enviando}
                    variant="outlined"
                    className="input-comentario"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleEnviarSeguimiento}
                    disabled={!oportunidadId || enviando || !comentario.trim()}
                    className="btn-enviar"
                    startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  >
                    {enviando ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Línea de Tiempo */}
          <div className="timeline-card">
            <div className="timeline-header">
              <div className="header-title">
                <History className="header-icon" />
                <h3>Historial de Actividades</h3>
              </div>
              <span className="contador-registros">
                {seguimientos.length} Registro{seguimientos.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="timeline-content">
              {seguimientos.length > 0 ? (
                <div className="timeline-list">
                  {seguimientos.map((seg, index) => (
                    <div key={seg.id} className="timeline-item">
                      <div className="timeline-marker">
                        <div 
                          className="marker-icon" 
                          style={{ backgroundColor: alpha(getTipoColor(seg.tipo), 0.1), borderColor: getTipoColor(seg.tipo) }}
                        >
                          {getTipoIcon(seg.tipo)}
                        </div>
                        {index !== seguimientos.length - 1 && <div className="marker-line"></div>}
                      </div>
                      
                      <div className="timeline-body">
                        <div className="timeline-meta">
                          <span 
                            className="tipo-tag" 
                            style={{ backgroundColor: alpha(getTipoColor(seg.tipo), 0.1), color: getTipoColor(seg.tipo) }}
                          >
                            {seg.tipo?.toUpperCase()}
                          </span>
                          <span className="fecha-tag">
                            <AccessTime fontSize="small" />
                            {new Date(seg.creado_en).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="timeline-descripcion">
                          {seg.descripcion}
                        </div>
                        
                        {seg.resultado && (
                          <div className="timeline-resultado">
                            <strong>Resultado:</strong> {seg.resultado}
                          </div>
                        )}
                        
                        <div className="timeline-footer">
                          <span className="responsable-tag">
                            <Avatar sx={{ width: 20, height: 20, fontSize: 10, mr: 1 }}>
                              {seg.responsable?.nombre?.[0] || 'V'}
                            </Avatar>
                            {seg.responsable?.nombre || 'Vendedor'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-timeline">
                  <Assignment className="empty-icon" />
                  <h4>Aún no hay gestiones registradas</h4>
                  <p>Registra tu primera actividad usando el formulario de arriba</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectoDetalle;